'use client';

import React, { useEffect, useState } from 'react';
import Link from 'next/link';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, Sparkles, ShoppingBag, Store, Bike } from 'lucide-react';

// Soft, premium gradient palettes matching brand colors
const GRADIENT_THEMES = [
	'bg-gradient-to-br from-[#051449] via-[#092370] to-[#004e92]', // Deep Blue (Official)
	'bg-gradient-to-br from-[#0f0c29] via-[#302b63] to-[#24243e]', // Night Purple
	'bg-gradient-to-br from-[#1c1c1c] via-[#2c3e50] to-[#000000]', // Sleek Dark
];

export default function HomeBanner({
	banners,
}: {
	banners: {
		id: string;
		title?: string;
		subtitle?: string;
		cta?: string;
		href?: string;
	}[];
}) {
	const [idx, setIdx] = useState(0);

	useEffect(() => {
		const t = setInterval(
			() => setIdx((i) => (i + 1) % banners.length),
			6000,
		);
		return () => clearInterval(t);
	}, [banners.length]);

	if (!banners || banners.length === 0) return null;

	const currentBanner = banners[idx];

	return (
		<div className="relative w-full overflow-hidden rounded-3xl shadow-2xl min-h-[450px] md:h-[500px] flex items-center justify-center py-12 md:py-0">
			
			{/* --- Animated Backgrounds --- */}
			<AnimatePresence mode="popLayout">
				<motion.div
					key={idx}
					initial={{ opacity: 0 }}
					animate={{ opacity: 1 }}
					exit={{ opacity: 0 }}
					transition={{ duration: 1.5 }}
					className={`absolute inset-0 ${GRADIENT_THEMES[idx % GRADIENT_THEMES.length]}`}
				/>
			</AnimatePresence>

			{/* --- Decorative Floating Elements (Code-drawn) --- */}
			<div className="absolute inset-0 overflow-hidden pointer-events-none">
				{/* Circle 1 */}
				<motion.div
					animate={{ y: [0, -20, 0], opacity: [0.3, 0.6, 0.3] }}
					transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
					className="absolute top-[-10%] left-[-5%] w-64 h-64 bg-white/5 rounded-full blur-3xl"
				/>
				{/* Circle 2 */}
				<motion.div
					animate={{ y: [0, 30, 0], scale: [1, 1.1, 1] }}
					transition={{ duration: 7, repeat: Infinity, ease: "easeInOut" }}
					className="absolute bottom-[-10%] right-[-5%] w-80 h-80 bg-[#FFCF23]/10 rounded-full blur-3xl"
				/>
				{/* Floating Icons */}
				<motion.div
					animate={{ y: [0, -15, 0], rotate: [0, 5, 0] }}
					transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
					className="absolute top-1/4 right-[15%] text-white/10"
				>
					<ShoppingBag size={120} />
				</motion.div>
				<motion.div
					animate={{ y: [0, 20, 0], rotate: [0, -10, 0] }}
					transition={{ duration: 6, repeat: Infinity, ease: "easeInOut", delay: 1 }}
					className="absolute bottom-1/4 left-[10%] text-white/5"
				>
					<Store size={100} />
				</motion.div>
			</div>

			{/* --- Content Content --- */}
			<div className="relative z-10 text-center max-w-4xl px-4">
				<AnimatePresence mode="wait">
					<motion.div
						key={currentBanner.id}
						initial={{ opacity: 0, y: 30 }}
						animate={{ opacity: 1, y: 0 }}
						exit={{ opacity: 0, y: -30 }}
						transition={{ duration: 0.8, ease: "easeOut" }}
						className="flex flex-col items-center"
					>
						{/* Badge */}
						<motion.div
							initial={{ scale: 0.8, opacity: 0 }}
							animate={{ scale: 1, opacity: 1 }}
							transition={{ delay: 0.3 }}
							className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/10 backdrop-blur-md border border-white/20 text-blue-100 text-sm font-medium mb-6"
						>
							<Sparkles className="w-4 h-4 text-[#FFCF23]" />
							<span>Welcome to Yunimall</span>
						</motion.div>

						{/* Title */}
						<h1 className="text-3xl md:text-6xl font-black text-white tracking-tight mb-4 md:mb-6 leading-tight drop-shadow-lg">
							{currentBanner.title}
						</h1>

						{/* Subtitle */}
						<p className="text-base md:text-2xl text-blue-100 mb-8 md:mb-10 max-w-2xl font-light leading-relaxed px-4 md:px-0">
							{currentBanner.subtitle}
						</p>

						{/* CTA */}
						<div className="flex flex-col sm:flex-row gap-4">
							<Link
								href={currentBanner.href || '/products'}
								className="group relative px-8 py-4 bg-[#FFCF23] text-[#051449] font-bold rounded-xl shadow-[0_0_20px_rgba(255,207,35,0.3)] hover:shadow-[0_0_30px_rgba(255,207,35,0.5)] transition-all transform hover:-translate-y-1 overflow-hidden"
							>
								<span className="relative z-10 flex items-center gap-2">
									{currentBanner.cta || 'Explore Now'}
									<ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
								</span>
								<div className="absolute inset-0 bg-white/20 translate-y-full group-hover:translate-y-0 transition-transform duration-300" />
							</Link>

							<Link
								href="/become-vendor"
								className="px-8 py-4 bg-transparent border-2 border-white/20 text-white font-bold rounded-xl hover:bg-white/10 transition-all backdrop-blur-sm"
							>
								Start Selling
							</Link>
							<Link
								href="/logistics/auth/register"
								className="inline-flex items-center justify-center gap-2 px-6 py-4 bg-white/10 border border-white/25 text-white font-bold rounded-xl hover:bg-white/20 transition-all backdrop-blur-sm"
							>
								<Bike className="w-5 h-5" />
								Become a Rider
							</Link>
						</div>
					</motion.div>
				</AnimatePresence>
			</div>

			{/* --- Slider Indicators --- */}
			<div className="absolute bottom-4 md:bottom-6 flex gap-3 z-20">
				{banners.map((_, i) => (
					<button
						key={i}
						onClick={() => setIdx(i)}
						className="group relative p-2"
						aria-label={`Go to slide ${i + 1}`}
					>
						<motion.div
							className={`w-12 h-1.5 rounded-full transition-colors duration-300 ${i === idx ? 'bg-[#FFCF23]' : 'bg-white/20 group-hover:bg-white/40'
								}`}
						>
							{i === idx && (
								<motion.div
									layoutId="activeIndicator"
									className="absolute inset-0 bg-[#FFCF23] rounded-full blur-[2px]"
									transition={{ duration: 0.3 }}
								/>
							)}
						</motion.div>
					</button>
				))}
			</div>
		</div>
	);
}
